/**
 * Pure XZ-plane geometry for the room split — the wall's own frame, and the
 * two FACE lines the room polygon is cut along.
 *
 * A room polygon runs along wall FACES, never centrelines (the pipeline's
 * polygons exclude wall bodies), so a partition cuts its room with TWO lines
 * half a thickness either side of its centreline. The strip between them is
 * the wall body and belongs to neither room — which is also why the two sides'
 * areas plus `thickness × span` come back to the original area, the invariant
 * the tests assert.
 *
 * Y is up; increasing Z is screen-down in the top-down view (CLAUDE.md).
 */

const { DEFAULT_WALL_THICKNESS } = require('../geometry/wallConstants');

/** Unit long-axis direction of a wall from its Y rotation (matches `axisOf`). */
function wallDir(wall) {
  const ry = wall?.rotation?.y || 0;
  return { x: Math.cos(ry), z: -Math.sin(ry) };
}

/**
 * The wall's cutting frame.
 *
 * `n` is the unit normal (left of `dir`); `cFront` / `cBack` are the two face
 * lines as offsets along `n`, so a point's side is the sign of `p·n - c`.
 */
function wallFrame(wall) {
  const dir = wallDir(wall);
  const n = { x: -dir.z, z: dir.x };
  const c = wall?.center || { x: 0, z: 0 };
  const length = wall?.dimensions?.x || 0;
  const thickness = wall?.dimensions?.z || DEFAULT_WALL_THICKNESS;
  const half = thickness / 2;
  const cCenter = c.x * n.x + c.z * n.z;
  return {
    dir,
    n,
    half,
    thickness,
    length,
    center: { x: c.x, z: c.z },
    cCenter,
    cFront: cCenter + half,
    cBack: cCenter - half,
    // Centreline endpoints — what must land on the room boundary for the wall
    // to SPAN the room.
    ends: [
      { x: c.x - dir.x * (length / 2), z: c.z - dir.z * (length / 2) },
      { x: c.x + dir.x * (length / 2), z: c.z + dir.z * (length / 2) },
    ],
  };
}

/** Signed distance of a point from a face line of the frame. */
function sideDistance(p, frame, c) {
  return p.x * frame.n.x + p.z * frame.n.z - c;
}

/** Distance from `p` to the segment `a`→`b`. */
function distanceToSegment(p, a, b) {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len2 = dx * dx + dz * dz;
  if (len2 < 1e-12) return Math.hypot(p.x - a.x, p.z - a.z);
  let t = ((p.x - a.x) * dx + (p.z - a.z) * dz) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.z - (a.z + t * dz));
}

/** Distance from `p` to the closest edge of a closed `{x,z}[]` ring. */
function distanceToPolygonBoundary(p, poly) {
  let best = Infinity;
  for (let i = 0; i < poly.length; i++) {
    const d = distanceToSegment(p, poly[i], poly[(i + 1) % poly.length]);
    if (d < best) best = d;
  }
  return best;
}

/** `{x,z}[]` of a room's first floor polygon, or `[]`. */
function roomFloorPolygonXZ(room) {
  const corners = room?.floors?.[0]?.polygonCorners;
  if (!corners?.length) return [];
  return corners.map((p) => ({ x: p.x, z: p.z }));
}

/**
 * A room wall's two endpoints.
 *
 * From its alignment corners where it has them, and otherwise DERIVED from its
 * centre, rotation and length -- the same fallback `room-merge/reparent.js`
 * already trusts to match an FK onto a wall. 3.4% of the corpus's room walls
 * (79 of 2316, across 10 rooms) carry no alignment corners at all, and treating
 * those as "no geometry to judge it by" put every one of them on whichever side
 * happened to be the fallback: one bathroom handed all eleven of its boundary
 * walls to the kept room and left the new one holding nothing but the
 * partition face, which is below the three walls a ceiling needs.
 *
 * `derived` marks the fallback, because a wall that has to be CLIPPED needs
 * real alignment corners to clip and the caller must supply them.
 */
function wallEndpoints(roomWall) {
  const bl = roomWall?.alignment?.bottomLeft;
  const br = roomWall?.alignment?.bottomRight;
  if (bl && br) return { bl, br };

  const c = roomWall?.center;
  const len = roomWall?.dimensions?.x;
  if (!c || !(len > 0)) return null;
  const ry = roomWall.rotation?.y || 0;
  const dir = { x: Math.cos(ry), z: -Math.sin(ry) };
  const half = len / 2;
  const y = (c.y ?? 0) - (roomWall.dimensions?.y || 0) / 2;
  return {
    bl: { x: c.x - dir.x * half, y, z: c.z - dir.z * half },
    br: { x: c.x + dir.x * half, y, z: c.z + dir.z * half },
    derived: true,
  };
}

module.exports = {
  wallDir,
  wallFrame,
  sideDistance,
  distanceToSegment,
  distanceToPolygonBoundary,
  roomFloorPolygonXZ,
  wallEndpoints,
};
